import { useState } from 'react';
import { motion } from 'framer-motion';

import { GenderFemale, GenderMale, MaxRoster, MinRoster } from '@/api/generated/game';
import { RoomMulti } from '@/api/generated/ws-protocol';
import { useAppRuntime } from '@/app/runtime';
import { selectIsHost, useActions, useGame } from '@/store/game';
import { MainAction } from '@/ui/MainAction';
import { RejectionNotice } from '@/ui/RejectionNotice';

import { playersWord, statusLabel } from './shared';

function genderLabel(gender: string): string {
  if (gender === GenderFemale) return 'Ж';
  if (gender === GenderMale) return 'М';
  return '—';
}

/**
 * Уже подключены к комнате (создали её или вошли по коду) и ждём начала.
 * Общий экран для hotseat и multi: список берётся из PublicState.players —
 * в hotseat он зафиксирован составом, который завёл хост, в multi пополняется
 * в реальном времени по мере подключения участников. Компонент их не различает,
 * потому что и не должен: это не правило игры, а одно и то же зрелище.
 */
export function ConnectedLobby() {
  const state = useGame((s) => s.state);
  const status = useGame((s) => s.status);
  const roomMode = useGame((s) => s.roomMode);
  const roomCode = useGame((s) => s.roomCode);
  const isHost = useGame(selectIsHost);
  const leave = useGame((s) => s.leave);
  const reset = useGame((s) => s.reset);
  const { start, finish } = useActions();
  const restClient = useAppRuntime()?.restClient ?? null;
  const [confirmingClose, setConfirmingClose] = useState(false);

  /**
   * Закрытие комнаты. По живому сокету это `finish`; когда сокета нет,
   * намерение отправить нечем — и ровно для этого случая есть
   * `DELETE /rooms/{code}` (шаг Н4). Без него хост, у которого оборвалась
   * связь, оставлял бы комнату висеть вместе с занятым кодом.
   */
  function closeRoom(): void {
    if (status === 'online') {
      finish();
      return;
    }
    const code = roomCode;
    if (!restClient || !code) return;
    // Отказ показывать некому и незачем: комната всё равно потеряна для нас,
    // а 404 здесь значит «уже закрыта» ровно так же, как «не хост».
    void restClient.closeRoom(code).catch(() => undefined);
    reset();
  }

  if (!state) return null; // вызывающий уже проверил phase === lobby при наличии state

  const players = [...state.players].sort((a, b) => a.seat - b.seat);
  const count = players.length;
  const canStart = count >= MinRoster && count <= MaxRoster && status === 'online';

  return (
    <div
      className="flex min-h-screen flex-col bg-ground px-5 text-ink"
      style={{ paddingTop: 'var(--clown-safe-top)' }}
    >
      <div className="flex-1 py-6">
        <h1 className="font-display text-task">КТО ИГРАЕТ</h1>

        {status !== 'online' && (
          <p className="mt-2 text-meta uppercase text-acc">{statusLabel(status)}</p>
        )}

        {/*
          В multi код — единственный способ позвать остальных: `Room.deepLink`
          сервер не заполняет (docs/backend-contract-changelog.md, шаг В10),
          собирать ссылку из выдуманных имён бота хуже, чем не собирать.
          В hotseat кода на экране нет: комнату по WS открывает только хост.
        */}
        {roomMode === RoomMulti && roomCode && (
          <div className="mt-4 border-2 border-line bg-panel p-4">
            <p className="text-meta uppercase text-mute">Код комнаты</p>
            <p className="mt-1 font-display text-display tracking-widest">{roomCode}</p>
            <p className="mt-1 text-base text-dim">Назовите его остальным — войдут по коду.</p>
          </div>
        )}

        <ul className="mt-3 flex flex-col gap-rule border-2 border-line bg-line">
          {/* Новый игрок в multi появляется сам собой: отдельного события
              «игрок вошёл» в протоколе нет, приезжает состав целиком, и
              единственное, что отличает нового, — эта анимация. */}
          {players.map((p) => (
            <motion.li
              key={p.id}
              layout
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, ease: [0.2, 0.8, 0.2, 1] }}
              className="flex h-row items-center gap-3 bg-panel px-4"
            >
              <span className="min-w-0 flex-1 truncate text-row">{p.name}</span>
              <span className="text-meta uppercase text-mute">{genderLabel(p.gender)}</span>
            </motion.li>
          ))}
          {players.length === 0 && (
            <li className="bg-panel px-4 py-4 text-base text-mute">Ждём игроков…</li>
          )}
        </ul>

        {/*
          Выключенная кнопка обязана объяснять себя. Нативная MainButton гаснет
          в самом Telegram, и без строки рядом это выглядит как приложение,
          которое просто не реагирует на нажатие.
        */}
        {!canStart && (
          <p className="mt-3 text-base text-mute">
            {status !== 'online'
              ? 'Нет связи с партией — начать не получится.'
              : count < MinRoster
                ? `Партия начинается от ${MinRoster} игроков.`
                : `За столом больше ${MaxRoster} игроков — столько партия не держит.`}
          </p>
        )}

        <RejectionNotice />

        {/*
          Разойтись, не начав, — обычный исход: собрались, передумали, ушли.
          Отдельной команды «отменить комнату» в контракте нет, закрывает её
          тот же `finish`: из стадии lobby он проходит (decideFinish проверяет
          только «партия не завершена» и «актор — хост»), сервер шлёт closed и
          сразу освобождает код. Своим `reset()` этого не добиться: он забывает
          комнату локально, а для остальных она остаётся живой, и по коду в неё
          продолжат входить.

          Подтверждение обязательно: у хоста это единственная кнопка, которая
          выкидывает из комнаты всех, а тапают её в темноте и навеселе.
        */}
        {isHost ? (
          confirmingClose ? (
            <div className="mt-6 flex flex-col gap-rule border-2 border-acc bg-line">
              <p className="bg-panel px-4 py-3 text-base text-ink">
                Закрыть комнату? Код освободится, остальным придётся собираться заново.
              </p>
              <button
                type="button"
                onClick={closeRoom}
                className="flex h-tap items-center justify-center bg-panel px-4 text-meta uppercase tracking-widest text-acc"
              >
                Да, закрыть
              </button>
              <button
                type="button"
                onClick={() => setConfirmingClose(false)}
                className="flex h-tap items-center justify-center bg-panel px-4 text-meta uppercase tracking-widest text-mute"
              >
                Отмена
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmingClose(true)}
              className="mt-6 flex h-tap w-full items-center justify-center border-2 border-line bg-panel text-meta uppercase tracking-widest text-mute"
            >
              Закрыть комнату
            </button>
          )
        ) : (
          // Не-хост чужую комнату закрыть не может. Его выход — сообщение
          // `leave` серверу, а не локальное забывание: иначе место останется
          // занятым до таймаута, и компания будет ждать призрака.
          <button
            type="button"
            onClick={leave}
            className="mt-6 flex h-tap w-full items-center justify-center border-2 border-line bg-panel text-meta uppercase tracking-widest text-mute"
          >
            Выйти из комнаты
          </button>
        )}
      </div>

      <div className="-mx-5" style={{ paddingBottom: 'var(--clown-safe-bottom)' }}>
        <MainAction
          text={`НАЧАТЬ · ${count} ${playersWord(count)}`}
          enabled={canStart}
          onPress={start}
        />
      </div>
    </div>
  );
}
